<template>
  <div v-if="show" class="mc-overlay" @click.self="close">
    <div class="mc-panel">
      <div class="mc-header">
        <h2>消息中心</h2>
        <button class="mc-close" @click="close">✕</button>
      </div>

      <!-- 工具栏 -->
      <div class="mc-toolbar">
        <input
          v-model="searchQ"
          class="mc-search"
          placeholder="搜索消息..."
          @input="applyFilter"
        />
        <select v-model="filterType" class="mc-select" @change="applyFilter">
          <option value="">全部类型</option>
          <option value="order_complete">订单完成</option>
          <option value="request_result">申请结果</option>
          <option value="account_change">账号变更</option>
          <option value="system">系统公告</option>
        </select>
        <select v-model="filterRead" class="mc-select" @change="applyFilter">
          <option value="">全部状态</option>
          <option value="0">未读</option>
          <option value="1">已读</option>
        </select>
        <select v-model="sortOrder" class="mc-select" @change="applyFilter">
          <option value="desc">最新优先</option>
          <option value="asc">最旧优先</option>
        </select>
      </div>

      <!-- 批量操作栏 -->
      <div class="mc-batch">
        <label class="mc-check-all">
          <input type="checkbox" :checked="allSelected" @change="toggleSelectAll" />
          全选
        </label>
        <span class="mc-batch-count">已选 {{ selected.length }}</span>
        <button
          v-if="selected.length > 0"
          class="mc-btn"
          @click="markSelectedRead"
        >标为已读</button>
        <button
          v-if="selected.length > 0"
          class="mc-btn mc-btn-danger"
          @click="deleteSelected"
        >删除</button>
        <button class="mc-btn mc-btn-ghost" @click="markAllRead">全部已读</button>
      </div>

      <!-- 消息列表 -->
      <div class="mc-list">
        <div v-if="loading" class="mc-empty">加载中...</div>
        <div v-else-if="messages.length === 0" class="mc-empty">暂无消息</div>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="mc-item"
          :class="{ 'mc-unread': !msg.is_read }"
          @click="readOne(msg)"
        >
          <label class="mc-checkbox" @click.stop>
            <input
              type="checkbox"
              :checked="selected.includes(msg.id)"
              @change="toggleSelect(msg.id)"
            />
          </label>
          <div class="mc-item-body">
            <div class="mc-item-header">
              <span class="mc-type-badge" :class="msg.type">{{ typeLabel(msg.type) }}</span>
              <span class="mc-time">{{ formatTime(msg.created_at) }}</span>
              <span v-if="!msg.is_read" class="mc-dot"></span>
            </div>
            <div class="mc-title">{{ msg.title }}</div>
            <div v-if="msg.content" class="mc-content">{{ msg.content }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'MessageCenter',

  data() {
    return {
      loading: false,
      searchQ: '',
      filterType: '',
      filterRead: '',
      sortOrder: 'desc',
      selected: []
    }
  },

  computed: {
    ...mapGetters('messages', ['showMessageCenter']),
    show() { return this.showMessageCenter },
    messages() { return this.$store.state.messages.messages },
    allSelected() {
      return this.messages.length > 0 && this.selected.length === this.messages.length
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.selected = []
        this.applyFilter()
      }
    }
  },

  methods: {
    close() {
      this.$store.dispatch('messages/closeMessageCenter')
    },

    async applyFilter() {
      this.loading = true
      await this.$store.dispatch('messages/fetchMessages', {
        q: this.searchQ || undefined,
        type: this.filterType || undefined,
        read: this.filterRead !== '' ? this.filterRead : undefined,
        sort: this.sortOrder
      })
      this.loading = false
      this.selected = []
    },

    toggleSelect(id) {
      const idx = this.selected.indexOf(id)
      if (idx === -1) this.selected.push(id)
      else this.selected.splice(idx, 1)
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selected = []
      } else {
        this.selected = this.messages.map(m => m.id)
      }
    },

    async readOne(msg) {
      if (!msg.is_read) {
        await this.$store.dispatch('messages/markRead', [msg.id])
      }
    },

    async markSelectedRead() {
      if (!this.selected.length) return
      await this.$store.dispatch('messages/markRead', [...this.selected])
      this.selected = []
    },

    async markAllRead() {
      await this.$store.dispatch('messages/markAllRead')
      this.selected = []
    },

    async deleteSelected() {
      if (!this.selected.length) return
      await this.$store.dispatch('messages/deleteMessages', [...this.selected])
      this.selected = []
    },

    typeLabel(type) {
      const map = {
        order_complete: '订单',
        request_result: '申请',
        account_change: '账号',
        system: '公告'
      }
      return map[type] || type
    },

    formatTime(dt) {
      if (!dt) return ''
      return dt.replace('T', ' ').slice(0, 16)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";
@import "@/styles/mixins.scss";

.mc-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 600;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 64px 16px 16px;

  @include mobile {
    align-items: flex-end;
    justify-content: center;
    padding: 0;
  }
}

.mc-panel {
  background: #fff;
  border-radius: 12px;
  width: 420px;
  max-width: 100%;
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  overflow: hidden;

  @include mobile {
    width: 100%;
    max-height: 92vh;
    border-radius: 16px 16px 0 0;
  }
}

.mc-header {
  @include flex-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid $color-border-light;
  h2 {
    font-size: 1rem;
    font-weight: 700;
    color: $color-text-primary;
    margin: 0;
  }
}

.mc-close {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: $color-text-muted;
  padding: 4px;
  &:hover { color: $color-text-primary; }
}

.mc-toolbar {
  display: flex;
  gap: 6px;
  padding: 10px 16px;
  border-bottom: 1px solid $color-border-light;
  flex-wrap: wrap;
}

.mc-search {
  flex: 1;
  min-width: 120px;
  padding: 5px 10px;
  border: 1px solid $color-border-light;
  border-radius: 6px;
  font-size: 0.8rem;
  outline: none;
  &:focus { border-color: $color-primary; }
}

.mc-select {
  padding: 5px 6px;
  border: 1px solid $color-border-light;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  outline: none;
  &:focus { border-color: $color-primary; }
}

.mc-batch {
  @include flex-center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid $color-border-light;
  background: #fafafa;
}

.mc-check-all {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  cursor: pointer;
}

.mc-batch-count {
  font-size: 0.75rem;
  color: $color-text-muted;
  flex: 1;
}

.mc-btn {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  border: 1px solid $color-primary;
  background: $color-primary;
  color: #fff;
  &:hover { opacity: 0.85; }

  &.mc-btn-danger {
    border-color: #ef4444;
    background: #ef4444;
  }

  &.mc-btn-ghost {
    background: none;
    color: $color-text-secondary;
    border-color: $color-border-light;
    &:hover { background: #f5f5f5; }
  }
}

.mc-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.mc-empty {
  text-align: center;
  padding: 40px 0;
  color: $color-text-muted;
  font-size: 0.9rem;
}

.mc-item {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid $color-border-light;
  transition: background 0.15s;
  &:last-child { border-bottom: none; }
  &:hover { background: #f9f9f9; }

  &.mc-unread { background: rgba($color-primary, 0.04); }
}

.mc-checkbox {
  padding-top: 2px;
  cursor: pointer;
}

.mc-item-body { flex: 1; min-width: 0; }

.mc-item-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.mc-type-badge {
  font-size: 0.65rem;
  padding: 1px 6px;
  border-radius: 4px;
  font-weight: 600;
  &.order_complete { background: #d1fae5; color: #065f46; }
  &.request_result { background: #dbeafe; color: #1e40af; }
  &.account_change { background: #fef3c7; color: #92400e; }
  &.system { background: #f3e8ff; color: #6b21a8; }
}

.mc-time {
  font-size: 0.7rem;
  color: $color-text-muted;
}

.mc-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ef4444;
  margin-left: auto;
}

.mc-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: $color-text-primary;
  margin-bottom: 2px;
}

.mc-content {
  font-size: 0.78rem;
  color: $color-text-secondary;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
